/* eslint-disable import/no-cycle */
import React, { useLayoutEffect } from "react";
import PropTypes, { number } from "prop-types";
import { connect } from "react-redux";
import useProminentNavigation from "../ExperimentGate/useProminentNavigation";
import FullScreenHomepageHeader from "./FullScreenHomepageHeader/FullScreenHomepageHeader";
// eslint-disable-next-line import/no-cycle
import Header from "./Header";
import endpoints from "../../shared/constants/endpoints";
import { isBrandRefreshEnabled } from "../../shared/utilities/brandRefresh";
import {
  isShippingReturnBarVisible,
  SHIPPING_RETURN_BAR_HEIGHT,
} from "./FullScreenHomepageHeader/ShippingReturnBar/ShippingReturnBar";

/** Fullscreen header body offset — must match rendered header height. */
export const FULLSCREEN_NAV_HEIGHT = 68;
export const FULLSCREEN_NAV_WITH_RIBBON_HEIGHT = 108;
/** Brand refresh: Figma nav 64 + ribbon 36. */
export const BRAND_REFRESH_NAV_HEIGHT = 64;
export const BRAND_REFRESH_NAV_WITH_RIBBON_HEIGHT = 100;

function HeaderSection({
  pathname,
  topHeaderOffsetDivHeight,
  userAddress,
  showDeliveryPickupAddressBar,
  showHeaderWidget,
  hideHeaderMenuOnScroll,
  userOrderMethod,
  showDeliveryPickupWidget,
}) {
  const prominentNavigation = useProminentNavigation();
  const isFullScreen = isBrandRefreshEnabled();

  useLayoutEffect(() => {
    const body = document.querySelector("body");

    const isCheckoutPage = pathname
      ? pathname.match(/checkout\/review-order/g)
      : false;
    const isLoginPage = pathname ? pathname.match(/login/g) : false;
    const isGiftingPage = pathname ? pathname.match(/gifting/g) : false;
    const isContactPage = pathname ? pathname.match(/contact/g) : false;
    const isCreateAccountPage = pathname
      ? pathname.match(/create-account/g)
      : false;
    const isLocationPage = pathname === endpoints.locations;
    const isdoughProPage = pathname === endpoints.doughProPage;
    const isCheckoutLoyaltyPage = pathname
      ? pathname.match(endpoints.orderLoyaltyRegex)
      : false;

    const resetPaddingOnPage =
      isCheckoutPage ||
      isLocationPage ||
      isdoughProPage ||
      isLoginPage ||
      isCreateAccountPage ||
      isCheckoutLoyaltyPage ||
      isGiftingPage ||
      isContactPage ||
      !showDeliveryPickupAddressBar;

    const isMenuPage = pathname === "/menu" || pathname?.match(/^\/menu\//);
    // Slug PDPs use /products/:slug (e.g. /products/9-pack-us), not only
    // /products/box|customize/. Sticky selection header owns the hide flag —
    // single PDPs never set it false, so collapsing on any /products/ is safe.
    // Anchor at start so /checkout/view-order/products/... is excluded.
    const isProductPage = !!pathname && /^\/products\//.test(pathname);
    const collapseTopNavChrome =
      ((isMenuPage && !prominentNavigation) || isProductPage) && !hideHeaderMenuOnScroll;
    const showShippingReturnBar = isShippingReturnBarVisible({
      showDeliveryPickupAddressBar,
      userOrderMethod,
      userAddress,
      showDeliveryPickupWidget,
      pathname,
    });
    const shippingBarOffset = showShippingReturnBar
      ? SHIPPING_RETURN_BAR_HEIGHT
      : 0;

    if (isFullScreen) {
      const isBrandRefresh = isBrandRefreshEnabled();
      const navHeight = isBrandRefresh
        ? BRAND_REFRESH_NAV_HEIGHT
        : FULLSCREEN_NAV_HEIGHT;
      const navWithRibbonHeight = isBrandRefresh
        ? BRAND_REFRESH_NAV_WITH_RIBBON_HEIGHT
        : FULLSCREEN_NAV_WITH_RIBBON_HEIGHT;
      // Product PDPs must reserve ribbon height when present (Carlos #18954) —
      // otherwise content sits too close / under the 100px chrome.
      const productChromeHeight =
        topHeaderOffsetDivHeight > 99 ? navWithRibbonHeight : navHeight;

      if (collapseTopNavChrome) {
        // Menu: reclaim vertical space when top nav hides on scroll.
        // Product PDPs: keep padding stable — collapsing it shifts the title
        // through IntersectionObserver and freezes the sticky header loop.
        if (isProductPage) {
          body.style.paddingTop = `${productChromeHeight + shippingBarOffset}px`;
          body.style.setProperty(
            "padding-top",
            `${productChromeHeight + shippingBarOffset}px`,
            "important",
          );
        } else {
          const collapsedTop = shippingBarOffset
            ? `${shippingBarOffset}px`
            : "0px";
          body.style.paddingTop = collapsedTop;
          body.style.setProperty("padding-top", collapsedTop, "important");
        }
      } else if (isMenuPage || isProductPage) {
        let pageChromeHeight;
        if (isProductPage) {
          pageChromeHeight = productChromeHeight;
        } else if (prominentNavigation) {
          pageChromeHeight = productChromeHeight;
        } else if (hideHeaderMenuOnScroll && isBrandRefresh) {
          pageChromeHeight = navWithRibbonHeight;
        } else {
          pageChromeHeight = navHeight;
        }
        body.style.paddingTop = `${pageChromeHeight + shippingBarOffset}px`;
        body.style.setProperty(
          "padding-top",
          `${pageChromeHeight + shippingBarOffset}px`,
          "important",
        );
      } else if (topHeaderOffsetDivHeight > 99) {
        body.style.paddingTop = `${navWithRibbonHeight + shippingBarOffset}px`;
        body.style.setProperty(
          "padding-top",
          `${navWithRibbonHeight + shippingBarOffset}px`,
          "important",
        );
      } else {
        body.style.paddingTop = `${navHeight + shippingBarOffset}px`;
        body.style.setProperty(
          "padding-top",
          `${navHeight + shippingBarOffset}px`,
          "important",
        );
      }
    } else if (resetPaddingOnPage) {
      body.className =
        topHeaderOffsetDivHeight > 99
          ? "pt-[115px] md:pt-[108px]"
          : "pt-[79px] md:pt-[68px]";
    } else if (userAddress) {
      body.className =
        topHeaderOffsetDivHeight > 99
          ? "pt-[176px] md:pt-[168px]"
          : "pt-[140px] md:pt-[128px]";
    } else if (!userAddress && pathname !== "/") {
      body.className =
        topHeaderOffsetDivHeight > 99
          ? "pt-[224px] md:pt-[168px]"
          : "pt-[188px] md:pt-[128px]";
    } else {
      body.className =
        topHeaderOffsetDivHeight > 99
          ? "pt-[224px] md:pt-[108px]"
          : "pt-[188px] md:pt-[68px]";
    }
  }, [
    topHeaderOffsetDivHeight,
    userAddress,
    pathname,
    showDeliveryPickupAddressBar,
    isFullScreen,
    prominentNavigation,
    showHeaderWidget,
    hideHeaderMenuOnScroll,
    userOrderMethod,
    showDeliveryPickupWidget,
  ]);

  return (
    <div className="relative w-full">
      <div className="top-0 w-full zIndex fixed">
        {isFullScreen ? (
          <FullScreenHomepageHeader
            location={{ pathname }}
            showDeliveryPickupAddressBar={showDeliveryPickupAddressBar}
          />
        ) : (
          <Header pathname={pathname} />
        )}
      </div>
    </div>
  );
}

HeaderSection.propTypes = {
  pathname: PropTypes.string,
  topHeaderOffsetDivHeight: number,
  userAddress: PropTypes.shape({ address1: PropTypes.string }),
  showDeliveryPickupAddressBar: PropTypes.bool,
  showHeaderWidget: PropTypes.bool,
  hideHeaderMenuOnScroll: PropTypes.bool,
  userOrderMethod: PropTypes.string,
  showDeliveryPickupWidget: PropTypes.bool,
};

HeaderSection.defaultProps = {
  pathname: null,
  topHeaderOffsetDivHeight: 0,
  userAddress: null,
  showDeliveryPickupAddressBar: true,
  showHeaderWidget: false,
  hideHeaderMenuOnScroll: true,
  userOrderMethod: null,
  showDeliveryPickupWidget: false,
};

export const mapStateToProps = (state) => {
  const {
    closeTimer,
    showHeaderWidget,
    hideHeaderMenuOnScroll,
    showDeliveryPickupWidget,
  } = state.elements;
  const { userAddress, userOrderMethod } = state.user;

  return {
    userAddress,
    userOrderMethod,
    closeTimer,
    showHeaderWidget,
    hideHeaderMenuOnScroll,
    showDeliveryPickupWidget,
  };
};

export default connect(mapStateToProps)(HeaderSection);
